// TechCare Concierge client. All AI work happens server-side (/api/concierge/*); this file only talks to our backend.
import { api, apiReady } from "./api";

const BASE = process.env.REACT_APP_BACKEND_URL;
const SID_KEY = "tc_concierge_sid";

export const quickActions = [
  { id: "services", label: "Explore Our Services" },
  { id: "quote", label: "Request a Quote" },
  { id: "site_survey", label: "Book a Site Survey" },
  { id: "track", label: "Track My Request" },
  { id: "contact", label: "Contact Our Team" },
];

export async function getConciergeStatus() {
  if (!apiReady) return { ai_enabled: false, mode: "offline" };
  try {
    const { data } = await api.get("/concierge/status");
    return { ai_enabled: Boolean(data?.ai_enabled), mode: data?.mode ?? "guided", limit_reached: Boolean(data?.limit_reached),
      total_timeout_s: Number(data?.total_timeout_s) || 45, safety_notice: data?.safety_notice ?? "" };
  } catch {
    return { ai_enabled: false, mode: "offline" };
  }
}

export async function getServicesKnowledge() {
  const { data } = await api.get("/concierge/knowledge", { params: { category: "service" } });
  return Array.isArray(data) ? data : [];
}

export async function ensureSession() {
  const existing = sessionStorage.getItem(SID_KEY);
  if (existing) return existing;
  const { data } = await api.post("/concierge/session");
  sessionStorage.setItem(SID_KEY, data.session_id);
  return data.session_id;
}

export async function clearSession() {
  const sid = sessionStorage.getItem(SID_KEY);
  sessionStorage.removeItem(SID_KEY);
  if (sid) await api.delete(`/concierge/session/${encodeURIComponent(sid)}`).catch(() => {});
}

// Streams one AI turn. `cid` is a per-message idempotency key: retrying with the same cid replays a stored answer
// instead of paying for a new one. onEvent receives {type: status|delta|draft|safety|privacy|error|done, ...}.
export async function streamMessage(message, cid, onEvent, timeoutS = 45, retried = false) {
  const sid = await ensureSession();
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), (timeoutS + 10) * 1000);
  try {
    const res = await fetch(`${BASE}/api/concierge/message`, {
      method: "POST", headers: { "Content-Type": "application/json" }, signal: ctrl.signal,
      body: JSON.stringify({ session_id: sid, message, client_msg_id: cid }),
    });
    if (res.status === 404 && !retried) {
      sessionStorage.removeItem(SID_KEY);
      clearTimeout(timer);
      return streamMessage(message, cid, onEvent, timeoutS, true);
    }
    if (!res.ok || !res.body) {
      let detail = "The assistant is unavailable right now. Please use the guided options or contact our team.";
      try { detail = (await res.json())?.detail || detail; } catch { /* keep default */ }
      onEvent({ type: "error", code: res.status === 503 ? "limit" : "unavailable", retry: res.status === 409 || (res.status >= 500 && res.status !== 503), text: detail });
      return;
    }
    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buf = "";
    for (;;) {
      const { value, done } = await reader.read();
      if (done) break;
      buf += decoder.decode(value, { stream: true });
      const chunks = buf.split("\n\n");
      buf = chunks.pop() ?? "";
      chunks.forEach((c) => {
        const line = c.split("\n").find((l) => l.startsWith("data: "));
        if (!line) return;
        try { onEvent(JSON.parse(line.slice(6))); } catch { /* ignore malformed chunk */ }
      });
    }
  } catch {
    onEvent({ type: "error", code: "timeout", retry: true,
      text: "The connection to the assistant was interrupted. You can retry, or use the guided options below." });
  } finally {
    clearTimeout(timer);
  }
}
